import {SnackBarActions} from '@store/aviary/aviary.adapter';
import {
  AUTH_PATRON_MISSING_MESSAGE,
  CUSTOM_ERROR_HANDLERS_CREATOR,
  CustomErrorHandlersCreator,
  ErrorActionMapper,
  REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE
} from '@aviary/interceptors/http-error-interceptor.types';
import {HttpContext} from '@angular/common/http';
import {BYPASS_400_ERROR} from '@aviary/store/http-loading/http-loading.types';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {PatronActions} from '@store/patron/patron.actions';

export function contextWithErrorHandlerForLoadAuthPatron(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // the authenticated patron was not found
          errorCodes: 'PATRON_INVALID_ID',
          actions: SnackBarActions.showError(AUTH_PATRON_MISSING_MESSAGE)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForLoadAccountStatus(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // the authenticated patron was not found
          errorCodes: 'ENTITY_ID_NOT_FOUND',
          actions: SnackBarActions.showError(AUTH_PATRON_MISSING_MESSAGE)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForLoadPatronReferenceOptions(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // the authenticated organization was not found
          errorCodes: 'GENERAL_INVALID_VALUE',
          actions: SnackBarActions.showError(`Library information not found. ${REFRESH_AND_TRY_AGAIN_ERROR_MESSAGE}`)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForChangePatronEmail(): HttpContext {
  /*
    For the email change dialog, a 'GENERAL_DUPLICATE_EMAIL' error will result in a validation error on the form field, so we don't want a redundant snack bar.
    We also don't want to display an error snack bar for otherwise unhandled errors in this context.
  */
  return contextWithErrorHandlerForPatronEmailChange().set(BYPASS_400_ERROR, true);
}

export function contextWithErrorHandlerForResendPatronEmailChangeVerification(): HttpContext {
  // For the resend button, we do want to show a snack bar for 'GENERAL_DUPLICATE_EMAIL' errors.
  return contextWithErrorHandlerForPatronEmailChange({
    errorCodes: 'GENERAL_DUPLICATE_EMAIL',
    actions: SnackBarActions.showError('An account with this email already exists.')
  });
}

export function contextWithErrorHandlerForCancelPatronEmailChange(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          errorCodes: 'PATRON_VERIFICATION_NOT_FOUND',
          actions: [
            SnackBarActions.show('There is no pending email change to cancel.'),
            PatronActions.reloadAuthPatron()
          ]
        },
        {
          errorCodes: 'PATRON_ONLINE_ACCESS_REQUIRED',
          actions: SnackBarActions.showError('This email change cannot be canceled online. Contact library staff for assistance.')
        },
        {
          errorCodes: 'PATRON_INVALID_ID',
          actions: SnackBarActions.showError(AUTH_PATRON_MISSING_MESSAGE)
        }
      ]
    }
  });

  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

// Internal only -- used above.
function contextWithErrorHandlerForPatronEmailChange(actionMapperForDuplicateEmail?: ErrorActionMapper): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        ...isDefined(actionMapperForDuplicateEmail) ? [actionMapperForDuplicateEmail] : [],
        {
          errorCodes: 'GENERAL_INVALID_VALUE',
          actions: SnackBarActions.showError('Invalid email.')
        },
        {
          errorCodes: 'PATRON_ONLINE_ACCESS_REQUIRED',
          actions: SnackBarActions.showError('An online account is required to change your email. Contact library staff for assistance.')
        },
        {
          errorCodes: 'PATRON_VERIFICATION_EMAIL_SEND_FAILED',
          actions: SnackBarActions.showError('The verification email could not be sent. Please try again.')
        },
        {
          errorCodes: 'PATRON_INVALID_ID',
          actions: SnackBarActions.showError(AUTH_PATRON_MISSING_MESSAGE)
        }
      ]
    }
  });

  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForVerifyPatronEmailChange(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          errorCodes: 'PATRON_VERIFICATION_ALREADY_USED',
          // Neutral snack bar because this isn't a problem from the user's point of view, nor is it a successful operation since they didn't cause a change.
          actions: SnackBarActions.show('Your email address has already been verified.')
        },
        {
          errorCodes: 'PATRON_VERIFICATION_EXPIRED',
          actions: SnackBarActions.showError('This verification link has expired. Please send a new verification email from your profile.')
        },
        {
          errorCodes: 'GENERAL_DUPLICATE_EMAIL',
          actions: SnackBarActions.showError('This email address is already in use by another account. Please change your email to a different address.')
        },
        {
          errorCodes: [
            'PATRON_VERIFICATION_CODE_INCORRECT', 'PATRON_VERIFICATION_INVALID_STATUS', 'PATRON_VERIFICATION_INVALID_TYPE',
            'PATRON_VERIFICATION_NOT_FOUND', 'ENTITY_ID_NOT_FOUND'
          ],
          actions: SnackBarActions.showError('This verification link is no longer valid. Please send a new verification email from your profile.')
        }
      ]
    }
  });

  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}


export function contextWithErrorHandlerForUpdatePatron(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // the requested branch id was not found
          errorCodes: 'BRANCH_INVALID_ID',
          actions: SnackBarActions.showError('Invalid branch selection.')
        },
        {
          // the requested pronouns object was not found
          errorCodes: 'ENTITY_ID_NOT_FOUND',
          actions: SnackBarActions.showError('Invalid pronouns selection.')
        },
        {
          // authenticated patron not found
          errorCodes: 'PATRON_INVALID_ID',
          actions: SnackBarActions.showError(AUTH_PATRON_MISSING_MESSAGE)
        }
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}

export function contextWithErrorHandlerForUpdateNotificationPreferences(): HttpContext {
  const handler: CustomErrorHandlersCreator = () => ({
    400: {
      discardOriginalErrorResponse: false,
      actionMappers: [
        {
          // the authenticated patron was not found
          errorCodes: 'ENTITY_ID_NOT_FOUND',
          actions: SnackBarActions.showError(AUTH_PATRON_MISSING_MESSAGE)
        },
        {
          // wrapper for requested preferences was missing
          errorCodes: 'GENERAL_MISSING_PARAM',
          actions: SnackBarActions.showError('Missing or invalid notification preferences.')
        },
        // errorCodes: 'GENERAL_INVALID_VALUE' - the api is supplying good specific errors for invalid parameters, intentionally not overriding it here
      ]
    }
  });
  return new HttpContext().set(CUSTOM_ERROR_HANDLERS_CREATOR, handler);
}
